import { FacilityCategory } from "../../Definitions/RealTimeAvailabilityTypes";

/**
 * Search Prefill Params — the RTA deep-link URL contract. Read once on mount and,
 * on the consumer, mirrored back as the user searches (buildSearchPrefillQuery).
 *
 *   ?q=Haircut                service query, seeds the q field (both layouts)
 *   ?filters=Massage,Nails    comma-separated facility `value` tokens; unmatched tokens dropped
 *   ?location=Austin,%20TX    location text, resolved via autocomplete (Layout 2 only)
 *   ?lat=30.27&lng=-97.74     resolved coordinates (Layout 2 only); fast-path that outranks `location`
 *   ?date=2026-07-14          service-gated local date (YYYY-MM-DD), past dates ignored
 *   ?time=14:00               service-gated 24h time (HH:MM); time-only → today, or tomorrow if past
 *
 * Layout 1 has its own coordinates, so `location`/`lat`/`lng` are dropped there. The comma is an
 * intra-value delimiter for `filters` only, so "Austin, TX" survives in `location`.
 */

export interface PrefillDescriptor {
    /** Layout 1 only. */
    serviceText?: string;
    /** Layout 2 only. Coordinates when a valid `lat`/`lng` pair is present,
     *  otherwise the `location` string to resolve via autocomplete. */
    location?: { query: string } | { lat: number; lng: number };
    /** Validated facility `value` tokens (mirrors activeBTypeFilter). */
    filterValues: string[];
    /** Service-gated local date. */
    date?: Date;
    /** Service-gated 24h time ("HH:MM"). */
    time?: string;
    /** Raw presence of a non-empty `filters` param — lets the consumer wait for the
     *  async facility-filter fetch before validating tokens and firing the search. */
    hasFilterParam: boolean;
}

function parseLocalDate(dateText: string): Date | undefined {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateText);
    if (!match) return undefined;

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(year, month - 1, day);

    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return undefined;
    }

    date.setHours(0, 0, 0, 0);
    return date;
}

function isPastDate(date: Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
}

/** Date to pair with a time-only deep link: today if the time is still upcoming,
 *  otherwise tomorrow — a past time on today's date would search an unbookable slot. */
function resolveTimeOnlyDate(time: string): Date {
    const [hour, minute] = time.split(":").map(Number);
    const now = new Date();
    const target = new Date();
    target.setHours(hour, minute, 0, 0);
    if (target <= now) target.setDate(target.getDate() + 1);
    target.setHours(0, 0, 0, 0);
    return target;
}

function normalizeTime(timeText: string): string | undefined {
    const match = /^(\d{1,2}):(\d{2})$/.exec(timeText);
    if (!match) return undefined;

    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return undefined;

    return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
}

function resolveFilterToken(token: string, canonicalByLowercase: Map<string, string>): string | undefined {
    const normalized = token.trim().toLowerCase();
    const exact = canonicalByLowercase.get(normalized);
    if (exact) return exact;

    // `filters` is comma-delimited, and this is the one live facility value that
    // contains a comma. Accept the stable prefix as a readable deep-link token.
    if (normalized === "buy now") {
        let matched: string | undefined;
        canonicalByLowercase.forEach((value) => {
            if (!matched && value.toLowerCase().startsWith("buy now,")) matched = value;
        });
        return matched;
    }

    return undefined;
}

/**
 * Pure normalizer: raw query string + layout + loaded facility categories →
 * the search intent to seed and fire on mount. No I/O, no DOM.
 */
export function parseSearchPrefill(
    queryString: string,
    layout: string,
    facilityCategories: FacilityCategory[],
): PrefillDescriptor {
    const params = new URLSearchParams(queryString);
    const descriptor: PrefillDescriptor = { filterValues: [], hasFilterParam: false };
    const isCrossLocation = layout === "2";

    const q = params.get("q")?.trim();
    if (q) descriptor.serviceText = q;

    if (isCrossLocation) {
        const lat = parseFloat(params.get("lat") ?? "");
        const lng = parseFloat(params.get("lng") ?? "");
        const location = params.get("location")?.trim();
        if (Number.isFinite(lat) && Number.isFinite(lng)
            && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
            descriptor.location = { lat, lng };
        } else if (location) {
            descriptor.location = { query: location };
        }
    }

    const filters = params.get("filters")?.trim();
    if (filters) {
        descriptor.hasFilterParam = true;
        const canonicalByLowercase = new Map<string, string>();
        for (const cat of facilityCategories) {
            for (const sub of cat.SubFacilities) {
                canonicalByLowercase.set(sub.value.toLowerCase(), sub.value);
            }
        }
        for (const token of filters.split(",")) {
            const canonical = resolveFilterToken(token, canonicalByLowercase);
            if (canonical && !descriptor.filterValues.includes(canonical)) {
                descriptor.filterValues.push(canonical);
            }
        }
    }

    if (descriptor.serviceText) {
        const dateText = params.get("date")?.trim();
        const parsedDate = dateText ? parseLocalDate(dateText) : undefined;
        const validDate = parsedDate && !isPastDate(parsedDate) ? parsedDate : undefined;
        if (validDate) descriptor.date = validDate;

        const timeText = params.get("time")?.trim();
        const normalizedTime = timeText ? normalizeTime(timeText) : undefined;
        if (normalizedTime) {
            descriptor.time = normalizedTime;
            if (!descriptor.date) {
                descriptor.date = resolveTimeOnlyDate(normalizedTime);
            }
        }
    }

    return descriptor;
}

export function hasSearchPrefill(descriptor: PrefillDescriptor): boolean {
    return !!descriptor.serviceText || !!descriptor.location
        || descriptor.hasFilterParam;
}

/** Live search state to project onto the deep-link URL (ADR 0005 §6 write-back). */
export interface SearchUrlState {
    /** RTA layout: "1" single-location, "2" cross-location. */
    layout: string;
    /** Service query text — the `q` key (both layouts). */
    serviceText?: string;
    /** Readable location label, e.g. "Austin, TX" — the `location` key (Layout 2 only); write-back
     *  uses the string, not `lat`/`lng`. Tri-state: a non-empty string sets it (an explicit place);
     *  `null`/`""` clears it (Clear Search); `undefined` leaves the existing param untouched, so a
     *  coordinate-only search never overwrites the last picked place. */
    locationLabel?: string | null;
    /** Selected facility `value` tokens (mirrors activeBTypeFilter) — the `filters` key. */
    filterValues: string[];
    /** Selected local date; written only alongside a `q`, mirroring the read-side gate. */
    date?: Date | null;
    /** Selected 24h time "HH:MM"; written only alongside a `q`. */
    time?: string | null;
}

/** Keys cleared then re-set on every write. Includes legacy `service`/`name`/`lat`/`lng` so a
 *  pasted deep link can't leave them behind. `location` is handled separately (can be preserved). */
const RECOMPUTED_PREFILL_KEYS = ["q", "service", "name", "filters", "lat", "lng", "date", "time"];

function formatLocalDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

/**
 * Inverse of parseSearchPrefill: projects the live search state onto the deep-link query
 * keys while preserving any unrelated params already on the URL. A URL built here reloads
 * through parseSearchPrefill to the same search, so write-back and read stay in lockstep.
 */
export function buildSearchPrefillQuery(state: SearchUrlState, existingSearch: string): string {
    const params = new URLSearchParams(existingSearch);
    for (const key of RECOMPUTED_PREFILL_KEYS) params.delete(key);

    const service = state.serviceText?.trim();
    if (service) params.set("q", service);

    // `location` is only rewritten for an explicit place pick (a freshly-resolved label). A
    // coordinate-only search passes `undefined` and leaves the last place untouched; `null`/`""`
    // clears it (Clear Search). Single-location layouts drop it.
    if (state.layout !== "2") {
        params.delete("location");
    } else if (state.locationLabel !== undefined) {
        const label = (state.locationLabel ?? "").trim();
        if (label) params.set("location", label);
        else params.delete("location");
    }

    if (state.filterValues.length > 0) {
        // `filters` is comma-delimited; the one live facility value that itself contains a
        // comma ("Buy Now, …") is written as its stable prefix so the split survives read.
        const tokens = state.filterValues.map(value =>
            value.includes(",") ? value.slice(0, value.indexOf(",")).trim() : value);
        params.set("filters", tokens.join(","));
    }

    // date/time only carry meaning next to a service query, matching parseSearchPrefill's gate.
    if (service) {
        if (state.date) params.set("date", formatLocalDate(state.date));
        if (state.time) params.set("time", state.time);
    }

    return params.toString();
}
